import { Typography, Form, FormInstance, Button, Alert, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { RecommendationComponent } from '../RecommendationComponent';
import {
	PredefinedReasonCategory,
	ReviewAction,
} from '../../../shared/Constants';
import { useZCRecommendationPage } from './useZCRecommendationPage';
import {
	Application,
	Feedback,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import useUserSession from '../../../hooks/useUserSession';
import { ALLOWED_ROLES } from '../../../shared/Utils';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface ZCRecommendationCollectionPageProps {
	form: FormInstance;
	zcRecommendation: Feedback | undefined;
	submitButtonDisabled: boolean;
	onSubmitClick: () => void;
	onCancelClick: () => void;
	application?: Application;
}

const SectionHeader = ({
	heading,
	description,
}: {
	heading: string;
	description: string;
}) => {
	return (
		<div className="flex flex-col mb-[8px]">
			<Text className="flex-grow text-primary-blue text-lg font-bold pb-4">
				{heading}
			</Text>
			<Text className="mb-[8px]" type="secondary">
				{description}
			</Text>
		</div>
	);
};

export const ZCRecommendationCollectionPage = ({
	form,
	zcRecommendation,
	submitButtonDisabled,
	onSubmitClick,
	onCancelClick,
	application,
}: ZCRecommendationCollectionPageProps) => {
	const { t } = useTranslation();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { selectedRole } = useUserSession();

	const isRoleNotAllowed = !ALLOWED_ROLES.includes(selectedRole as UserRole);

	const disableSubmit = submitButtonDisabled || isRoleNotAllowed;

	return (
		<>
			<div
				className={`flex flex-col min-h-0 bg-white mx-auto rounded-lg mt-4 border border-solid border-gray-200 ${
					isMobile ? 'w-full p-4' : 'w-3/4 max-w-[56%] p-8'
				}`}
			>
				<SectionHeader
					heading={t('provideZCFinalRecommendation')}
					description={t('pleaseProvideBasedOnAllInterviews')}
				/>
				<Form
					name="basic"
					layout="vertical"
					size="middle"
					labelCol={{ span: 16 }}
					initialValues={{
						remember: true,
						review: !zcRecommendation && ReviewAction.APPROVE,
					}}
					autoComplete="off"
					form={form}
				>
					<RecommendationComponent
						commentsMandatory
						commentsLabel="yourCommentsToSupportAboveRecommendation"
						commentsPlaceholder="commentsAreMandatory"
						form={form}
						category={PredefinedReasonCategory.ZC_REJECT}
						displayComments
						disabled={isRoleNotAllowed}
						application={application}
						recommendationBy={UserRole.ZC}
					/>
				</Form>
				{application?.isApprover && (
					<Alert
						showIcon
						message={t('onlyZCIsAllowedToSubmitFeedback')}
						type="warning"
						className="bg-orange-10 mb-3"
					/>
				)}
				<div className="flex flex-row p-2 justify-center">
					<Button onClick={onCancelClick}>{t('cancel')}</Button>
					<Button
						onClick={onSubmitClick}
						type="primary"
						htmlType="submit"
						disabled={disableSubmit}
						className="ml-[16px]"
					>
						{t('submit')}
					</Button>
				</div>
			</div>
		</>
	);
};

export const ZCRecommendationCollectionPageContainer = () => {
	const {
		form,
		application,
		onSubmitClick,
		onCancelClick,
		submitButtonDisabled,
	} = useZCRecommendationPage();
	return (
		<>
			<ZCRecommendationCollectionPage
				form={form}
				zcRecommendation={application?.zcRecommendation}
				submitButtonDisabled={submitButtonDisabled}
				onSubmitClick={onSubmitClick}
				onCancelClick={onCancelClick}
				application={application}
			/>
		</>
	);
};
